import { useEffect, useState } from 'react'

import { FxAlert, FxButton, FxIcon } from '@/components/fx'

import { ProjektDialog } from './ProjektDialog'
import type { Folder } from './useProjects'

/**
 * "In Projekt verschieben": one radio group over the projects, submitted as
 * `PATCH /v1/templates/:id {folder}`.
 *
 * A radio group rather than the artboard's implied picker list, because the
 * choice is one of a set and a keyboard user should reach it with one Tab and
 * the arrow keys. There is no "kein Projekt" option: this dialog is opened
 * from rows that have none.
 */
export function MoveTemplateDialog({
  open,
  templateTitle,
  folders,
  pending,
  error,
  onSubmit,
  onOpenChange,
}: {
  open: boolean
  templateTitle: string
  folders: Folder[]
  pending?: boolean
  error?: string | null
  onSubmit: (folderId: string) => void
  onOpenChange: (open: boolean) => void
}) {
  const [selected, setSelected] = useState('')

  useEffect(() => {
    if (open) setSelected('')
  }, [open, templateTitle])

  return (
    <ProjektDialog
      open={open}
      onOpenChange={onOpenChange}
      title="In Projekt verschieben"
      description={<>„{templateTitle}“ einem Projekt zuordnen.</>}
    >
      {folders.length === 0 ? (
        <FxAlert tone="info">
          Es gibt noch kein Projekt. Legen Sie zuerst über „Neues Projekt“ eines an.
        </FxAlert>
      ) : (
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault()
            if (!selected || pending) return
            onSubmit(selected)
          }}
        >
          <fieldset className="m-0 flex max-h-72 flex-col gap-1 overflow-y-auto border-0 p-0">
            <legend className="sr-only">Projekt auswählen</legend>
            {folders.map((folder) => (
              <label
                key={folder.id}
                className="flex h-list-row cursor-pointer items-center gap-4 rounded-sm px-4 hover:bg-primary-subtle"
              >
                <input
                  type="radio"
                  name="projekt"
                  value={folder.id}
                  checked={selected === folder.id}
                  onChange={() => setSelected(folder.id)}
                  className="h-5 w-5 flex-none accent-[hsl(var(--fx-primary-strong))]"
                />
                <FxIcon name="folder" size={22} filled className="flex-none text-primary-tonal" />
                <span className="min-w-0 flex-1 truncate">{folder.name}</span>
              </label>
            ))}
          </fieldset>
          {error && <FxAlert tone="danger">{error}</FxAlert>}
          <div className="flex flex-wrap justify-end gap-4">
            <FxButton variant="outlined" onClick={() => onOpenChange(false)}>
              Abbrechen
            </FxButton>
            <FxButton type="submit" disabled={!selected || pending}>
              Verschieben
            </FxButton>
          </div>
        </form>
      )}
    </ProjektDialog>
  )
}
